import { z } from '@hono/zod-openapi';

/** Practical browser/CDN ceiling; also bounds the DB column and every log line. */
export const MAX_WEBHOOK_URL_LENGTH = 2048;

/** Env URLs such as SESSION_BASE_URL, which is `http://localhost:5173` in local dev. */
export const AbsoluteHttpUrlSchema = z.url({ protocol: /^https?$/ });

/** HTTP Basic credentials carried in a webhook URL's userinfo (`https://user:password@host`). */
export interface WebhookCredentials {
  username: string;
  password: string;
}

const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/**
 * Remove userinfo from a URL, for anything that is logged or requested.
 * Never throws: a string `URL` cannot parse still loses its `user:password@`.
 */
export function stripUrlCredentials(value: string): string {
  try {
    const url = new URL(value);
    if (!url.username && !url.password) {
      return value;
    }
    url.username = '';
    url.password = '';
    return url.href;
  } catch {
    return value.replace(/^([a-z][a-z0-9+.-]*:\/\/)[^/?#]*@/i, '$1');
  }
}

/**
 * Split a webhook URL into the address to request and the Basic credentials
 * its userinfo carried. `credentials` is absent when the URL has no userinfo;
 * a username without a password yields an empty password, as curl does.
 *
 * Userinfo is percent-decoded, so `p%40ss` means the password `p@ss`.
 *
 * @throws when the userinfo cannot be sent as Basic auth (RFC 7617): escapes
 *   that are not UTF-8, a `:` in the username (it is the separator), or
 *   control characters. `WebhookUrlSchema` rejects those up front.
 */
export function splitWebhookUrl(value: string): { url: string; credentials?: WebhookCredentials } {
  const parsed = new URL(value);
  if (!parsed.username && !parsed.password) {
    return { url: value };
  }

  const username = decodeURIComponent(parsed.username);
  const password = decodeURIComponent(parsed.password);
  if (username.includes(':') || CONTROL_CHARACTERS.test(username + password)) {
    throw new Error('Webhook URL credentials cannot be sent as Basic auth');
  }

  return { url: stripUrlCredentials(value), credentials: { username, password } };
}

function hasSendableCredentials(value: string): boolean {
  try {
    splitWebhookUrl(value);
    return true;
  } catch {
    return false;
  }
}

/**
 * Caller-supplied webhook target. Same hostname bar as `z.httpUrl()` (Zod's
 * domain regex), but https-only — the Worker POSTs VIN, plate and photo URLs.
 *
 * `z.httpUrl()` itself allows http, so it is not used here.
 *
 * Userinfo (`https://user:password@host`) is allowed: delivery sends it as an
 * `Authorization: Basic` header and requests the URL without it. The hostname
 * check runs on the parsed host, so `https://public.example@10.0.0.1/` is
 * still rejected.
 */
export const WebhookUrlSchema = z
  .url({
    protocol: /^https$/,
    hostname: z.regexes.domain,
    abort: true,
    error: (issue) => {
      if (issue.note === 'Invalid protocol') {
        return 'Must use https (http is not accepted; vehicle data must not cross the network in cleartext)';
      }
      if (issue.note === 'Invalid hostname') {
        return 'Must be a public hostname (loopback, private, link-local and single-label hosts are not accepted)';
      }
      return 'Must be a valid absolute URL';
    },
  })
  .max(MAX_WEBHOOK_URL_LENGTH, { error: `Must be at most ${MAX_WEBHOOK_URL_LENGTH} characters` })
  .refine(hasSendableCredentials, {
    error:
      'Embedded credentials (user:password@host) must be percent-encoded UTF-8, without ":" in the username or control characters',
  });

export function isAbsoluteHttpUrl(value: string): boolean {
  return AbsoluteHttpUrlSchema.safeParse(value).success;
}
